import { useEffect, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Icon } from './icons';

/**
 * Primitives du design system (ADR-092, référence « ULTIM APP ») : bouton `Btn`, pastille `Pill`,
 * en-tête de page `PageHeader` (surtitre, titre, 3e ligne, onglets d'entité, actions, menu « … »),
 * bandeau de KPI `KpiBand`, et les pièces de la liste (ADR-094) : `ListToolbar`, `SelectMenu`,
 * `QuickFilterTabs`, `QuickFilterChips`, `TableFooterTotals` (totaux de pied, ADR-095). Couleurs, rayons et
 * polices par jetons uniquement (styles.css).
 */

export type BtnVariant = 'primary' | 'dark' | 'ghost' | 'soft' | 'success' | 'danger';

export function Btn({ variant = 'ghost', icon, children, className, type = 'button', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; icon?: string }) {
  return (
    <button {...rest} type={type} className={`ay-button ay-button--${variant}${className === undefined ? '' : ` ${className}`}`}>
      {icon !== undefined && <Icon name={icon} size={14} />}
      <span>{children}</span>
    </button>
  );
}

/** Tons de pastille : `success` se lit sur les jetons `color.tone.ok.*`. */
/** Initiales d'un libellé : deux lettres, la première et la dernière partie. */
export function initialsOf(label: string): string {
  const parts = label.trim().split(/\s+/).filter((part) => part !== '' && part !== '—');
  return (parts.length === 0 ? '?' : parts.length === 1 ? parts[0]!.slice(0, 2) : `${parts[0]![0]}${parts[parts.length - 1]![0]}`).toUpperCase();
}

/** Teinte déterministe d'un avatar (1 à 8) : une personne ou un objet garde sa couleur partout (ADR-100). */
export function avatarTone(label: string): number {
  let hash = 0;
  for (const char of label) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return (hash % 8) + 1;
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Avatar (ADR-100) : initiales sur une tuile de couleur déterministe, contraste AA garanti par les
 * jetons `--ay-avatar-<n>`. L'image d'un fichier gouverné viendra avec le service de fichiers ;
 * aucune URL de donnée n'est chargée ici.
 */
/**
 * Avatar (ADR-100, ADR-103) : l'image servie par une URL signée quand il y en a une, les initiales sur
 * une tuile de couleur déterministe en attendant ou si l'image ne se charge pas.
 */
export function Avatar({ label, size = 'md', shape = 'rounded', icon, src }: { label: string; size?: AvatarSize; shape?: 'circle' | 'rounded'; icon?: string; src?: string | undefined }) {
  const tone = avatarTone(label);
  const [failed, setFailed] = useState<string | null>(null);
  const image = src !== undefined && failed !== src ? src : undefined;
  return (
    <span className={`ay-avatar ay-avatar--${size} ay-avatar--${shape} ay-avatar--t${tone}${image === undefined ? '' : ' ay-avatar--image'}`} aria-hidden="true">
      {image !== undefined ? <img src={image} alt="" loading="lazy" decoding="async" onError={() => setFailed(image)} /> : icon !== undefined ? <Icon name={icon} size={size === 'xs' || size === 'sm' ? 14 : 20} /> : initialsOf(label)}
    </span>
  );
}

export type PillTone = 'neutral' | 'accent' | 'warn' | 'danger' | 'success' | 'info';

export function Pill({ tone = 'neutral', children }: { tone?: PillTone; children: ReactNode }) {
  return <span className={`ay-pill ay-pill--${tone === 'success' ? 'ok' : tone}`}>{children}</span>;
}

export interface MenuAction {
  id: string;
  label: string;
  icon?: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Raison d'un refus, affichée sous l'action grisée (refus visible d'avance). */
  hint?: string;
  /** Action destructrice ou irréversible : en rouge, après un séparateur. */
  tone?: 'danger';
  /** Trait de séparation avant l'action (groupes d'actions). */
  separator?: boolean;
}

/** Bouton « … » et son menu d'actions secondaires (`role="menu"`, clavier). */
export function ActionsMenu({ actions, label = 'Autres actions' }: { actions: readonly MenuAction[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!open) return;
    queueMicrotask(() => menu.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus());
    const onDown = (event: MouseEvent): void => {
      if (wrap.current !== null && event.target instanceof Node && !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (actions.length === 0) return null;
  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };
  const onKey = (event: ReactKeyboardEvent<HTMLUListElement>): void => {
    const entries = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [])];
    const index = entries.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown') entries[(index + 1) % entries.length]?.focus();
    else if (event.key === 'ArrowUp') entries[(index - 1 + entries.length) % entries.length]?.focus();
    else if (event.key === 'Escape') close();
    else if (event.key === 'Tab') return setOpen(false);
    else return;
    event.preventDefault();
  };
  return (
    <div className="ay-actionsmenu" ref={wrap}>
      <button ref={trigger} type="button" className="ay-button ay-button--ghost ay-button--icon" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="more" size={16} />
      </button>
      {open && (
        <ul ref={menu} className="ay-actionsmenu__list" role="menu" aria-label={label} onKeyDown={onKey}>
          {actions.map((action) => [
            action.separator === true ? <li key={`${action.id}-sep`} role="separator" className="ay-actionsmenu__sep" /> : null,
            <li key={action.id} role="none">
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                onMouseEnter={(event) => event.currentTarget.focus()}
                aria-disabled={action.disabled === true ? true : undefined}
                className={action.tone === 'danger' ? 'ay-actionsmenu__item--danger' : undefined}
                onClick={() => {
                  if (action.disabled === true) return;
                  setOpen(false);
                  action.onSelect();
                }}
              >
                <span className="ay-actionsmenu__icon">{action.icon !== undefined && <Icon name={action.icon} size={16} />}</span>
                <span className="ay-actionsmenu__text">
                  <span>{action.label}</span>
                  {action.hint !== undefined && <small>{action.hint}</small>}
                </span>
              </button>
            </li>,
          ])}
        </ul>
      )}
    </div>
  );
}

export interface EntityTab {
  entity_id: string;
  label: string;
  count?: number;
}

/**
 * Onglets d'entité (`role="tablist"`) : options venues de `context.entities`. Masqués s'il n'y a
 * qu'une entité ; désactivés tant qu'aucun filtre par entité n'est branché sur la liste.
 */
export function EntityTabs({ entities, selected, onSelect }: { entities: readonly EntityTab[]; selected?: string; onSelect?: (entityId: string) => void }) {
  if (entities.length < 2) return null;
  const disabled = onSelect === undefined;
  return (
    <div className="ay-entitytabs" role="tablist" aria-label="Entités">
      {entities.map((entity) => (
        <button
          key={entity.entity_id}
          type="button"
          role="tab"
          aria-selected={entity.entity_id === selected}
          disabled={disabled}
          title={disabled ? 'Filtre par entité : bientôt disponible' : undefined}
          className="ay-entitytabs__tab"
          onClick={() => onSelect?.(entity.entity_id)}
        >
          {entity.label}
          {entity.count !== undefined && <span className="ay-entitytabs__count">{entity.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** En-tête de page à trois lignes : surtitre, titre (police d'affichage), sous-titre ; puis onglets et actions. */
export function PageHeader({
  title,
  eyebrow,
  subtitle,
  titleId,
  tabs,
  actions,
  more,
  badge,
}: {
  title: ReactNode;
  eyebrow?: string;
  subtitle?: ReactNode;
  titleId?: string;
  tabs?: ReactNode;
  actions?: ReactNode;
  more?: readonly MenuAction[];
  badge?: ReactNode;
}) {
  return (
    <header className="ay-pageheader">
      <div className="ay-pageheader__text">
        {eyebrow !== undefined && <div className="ay-pageheader__eyebrow">{eyebrow}</div>}
        <div className="ay-pageheader__titleline">
          <h1 id={titleId} className="ay-pageheader__title">
            {title}
          </h1>
          {badge}
        </div>
        {subtitle !== undefined && <div className="ay-pageheader__subtitle">{subtitle}</div>}
      </div>
      {(tabs !== undefined || actions !== undefined || (more !== undefined && more.length > 0)) && (
        <div className="ay-pageheader__side">
          {tabs}
          {actions}
          {more !== undefined && <ActionsMenu actions={more} />}
        </div>
      )}
    </header>
  );
}

export interface KpiCard {
  id: string;
  label: string;
  value: string;
  unit?: string;
}

/** Bandeau de KPI (libellé en capitales, valeur en police d'affichage, unité) et badge d'alerte à droite. */
export function KpiBand({ cards, alert }: { cards: readonly KpiCard[]; alert?: string }) {
  if (cards.length === 0 && alert === undefined) return null;
  return (
    <section className="ay-kpiband" aria-label="Indicateurs">
      {cards.map((card) => (
        <article key={card.id} className="ay-kpiband__card" aria-label={card.label}>
          <div className="ay-kpiband__label">{card.label}</div>
          <div className="ay-kpiband__value">
            {card.value}
            {card.unit !== undefined && <span className="ay-kpiband__unit">{card.unit}</span>}
          </div>
        </article>
      ))}
      {alert !== undefined && (
        <div className="ay-kpiband__alert">
          <Pill tone="danger">
            <Icon name="alert" size={12} /> {alert}
          </Pill>
        </div>
      )}
    </section>
  );
}

export type ListMode = 'table' | 'cards';

export interface SelectOption {
  id: string;
  label: string;
}

/**
 * Sélecteur de barre d'outils (« Trier : Date », « Période : Ce mois-ci ») : bouton et menu de
 * choix exclusifs (`menuitemradio`), clavier comme `ActionsMenu`.
 */
export function SelectMenu({ label, options, selected, onSelect, icon }: { label: string; options: readonly SelectOption[]; selected?: string; onSelect: (id: string) => void; icon?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!open) return;
    queueMicrotask(() => (menu.current?.querySelector<HTMLElement>('[aria-checked="true"]') ?? menu.current?.querySelector<HTMLElement>('[role="menuitemradio"]'))?.focus());
    const onDown = (event: MouseEvent): void => {
      if (wrap.current !== null && event.target instanceof Node && !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (options.length === 0) return null;
  const current = options.find((option) => option.id === selected);
  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };
  const onKey = (event: ReactKeyboardEvent<HTMLUListElement>): void => {
    const entries = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? [])];
    const index = entries.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown') entries[(index + 1) % entries.length]?.focus();
    else if (event.key === 'ArrowUp') entries[(index - 1 + entries.length) % entries.length]?.focus();
    else if (event.key === 'Escape') close();
    else if (event.key === 'Tab') return setOpen(false);
    else return;
    event.preventDefault();
  };
  return (
    <div className="ay-actionsmenu ay-selectmenu" ref={wrap}>
      <button ref={trigger} type="button" className="ay-selectmenu__trigger" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        {icon !== undefined && <Icon name={icon} size={14} />}
        <span>{label}</span>
        {current !== undefined && (
          <>
            <span aria-hidden="true"> : </span>
            <b>{current.label}</b>
          </>
        )}
        <Icon name="chevron-down" size={13} />
      </button>
      {open && (
        <ul ref={menu} className="ay-actionsmenu__list" role="menu" aria-label={label} onKeyDown={onKey}>
          {options.map((option) => (
            <li key={option.id} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={option.id === selected}
                tabIndex={-1}
                onClick={() => {
                  setOpen(false);
                  onSelect(option.id);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface ToolbarSearch {
  value: string;
  onChange: (value: string) => void;
  /** Entrée : applique tout de suite, sans attendre l'anti-rebond. */
  onSubmit?: () => void;
  /** Croix d'effacement (et Échap). */
  onClear?: () => void;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * Barre d'outils de liste (design system, `preview/list.html`) : recherche (« / » pour y aller,
 * Entrée, croix), contrôles de la liste (filtres, tri, période), puis rafraîchir, exporter,
 * bascule de mode. Présentation seulement : le serveur autorise chaque lecture et chaque export.
 */
export function ListToolbar({
  search,
  onSearch,
  children,
  sorts,
  onRefresh,
  onExport,
  mode,
  onMode,
  trailing,
}: {
  search?: ToolbarSearch | string;
  /** Forme simple : recherche contrôlée par sa seule valeur. */
  onSearch?: (value: string) => void;
  children?: ReactNode;
  sorts?: readonly MenuAction[];
  onRefresh?: () => void;
  onExport?: () => void;
  mode?: ListMode;
  onMode?: (mode: ListMode) => void;
  /**
   * Contrôles à place fixe, à droite après l'espace (sélecteur de représentation, ADR-096) : ils ne
   * bougent pas quand les contrôles de gauche (tri, période) apparaissent ou disparaissent.
   */
  trailing?: ReactNode;
}) {
  const box: ToolbarSearch | undefined = typeof search === 'object' ? search : onSearch === undefined ? undefined : { value: search ?? '', onChange: onSearch };
  return (
    <div className="ay-listtoolbar" role="toolbar" aria-label="Outils de la liste">
      {box !== undefined && (
        <div className="ay-listtoolbar__search">
          <Icon name="search" size={14} />
          <input
            ref={box.inputRef}
            type="search"
            aria-label="Rechercher"
            placeholder={box.placeholder ?? 'Rechercher…'}
            value={box.value}
            onChange={(event) => box.onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                box.onSubmit?.();
              } else if (event.key === 'Escape' && box.value !== '' && box.onClear !== undefined) {
                event.preventDefault();
                box.onClear();
              }
            }}
          />
          {box.value !== '' && box.onClear !== undefined ? (
            <button type="button" className="ay-listtoolbar__clear" aria-label="Effacer la recherche" title="Effacer la recherche" onClick={box.onClear}>
              <Icon name="close" size={12} />
            </button>
          ) : (
            <kbd className="ay-listtoolbar__kbd" aria-hidden="true">
              /
            </kbd>
          )}
        </div>
      )}
      {children}
      {sorts !== undefined && <ActionsMenu actions={sorts} label="Trier" />}
      <span className="ay-listtoolbar__spacer" />
      {trailing}
      {onRefresh !== undefined && (
        <button type="button" className="ay-button ay-button--ghost ay-button--icon" aria-label="Rafraîchir" title="Rafraîchir" onClick={onRefresh}>
          <Icon name="refresh" size={14} />
        </button>
      )}
      {onExport !== undefined && (
        <Btn variant="ghost" icon="download" onClick={onExport}>
          Exporter
        </Btn>
      )}
      {mode !== undefined && onMode !== undefined && (
        <div className="ay-listtoolbar__modes" role="radiogroup" aria-label="Affichage">
          {(['table', 'cards'] as const).map((value) => (
            <button key={value} type="button" role="radio" aria-checked={mode === value} aria-label={value === 'table' ? 'Tableau' : 'Cartes'} title={value === 'table' ? 'Tableau' : 'Cartes'} className="ay-button ay-button--soft ay-button--icon" onClick={() => onMode(value)}>
              <Icon name={value === 'table' ? 'table' : 'cards'} size={14} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Onglets de filtre rapide (`role="tablist"`), compteur facultatif : un seul actif ; flèches
 * gauche / droite entre onglets.
 */
export function QuickFilterTabs({ items, selected, counts, onSelect, label = 'Filtres rapides' }: { items: readonly SelectOption[]; selected?: string; counts?: Readonly<Record<string, number | undefined>>; onSelect: (id: string) => void; label?: string }) {
  const list = useRef<HTMLDivElement | null>(null);
  if (items.length === 0) return null;
  const onKey = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const index = items.findIndex((item) => item.id === selected);
    const next = items[(index + (event.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length];
    if (next === undefined) return;
    event.preventDefault();
    onSelect(next.id);
    queueMicrotask(() => list.current?.querySelector<HTMLElement>(`[data-tab="${next.id}"]`)?.focus());
  };
  return (
    <div ref={list} className="ay-quicktabs" role="tablist" aria-label={label} onKeyDown={onKey}>
      {items.map((item) => {
        const count = counts?.[item.id];
        return (
          <button key={item.id} type="button" role="tab" data-tab={item.id} aria-selected={item.id === selected} tabIndex={item.id === selected ? 0 : -1} className="ay-quicktabs__tab" onClick={() => onSelect(item.id)}>
            {item.label}
            {count !== undefined && <small className="ay-quicktabs__count">{count.toLocaleString('fr')}</small>}
          </button>
        );
      })}
    </div>
  );
}

/** Jetons de filtre rapide (`aria-pressed`) : un seul actif. */
export function QuickFilterChips({ items, selected, counts, onSelect, label = 'Filtres rapides' }: { items: readonly SelectOption[]; selected?: string; counts?: Readonly<Record<string, number | undefined>>; onSelect: (id: string) => void; label?: string }) {
  if (items.length === 0) return null;
  return (
    <div className="ay-quickchips" role="group" aria-label={label}>
      {items.map((item) => {
        const count = counts?.[item.id];
        return (
          <button key={item.id} type="button" className="ay-quickchips__chip" aria-pressed={item.id === selected} onClick={() => onSelect(item.id)}>
            {item.label}
            {count !== undefined && <small className="ay-quicktabs__count">{count.toLocaleString('fr')}</small>}
          </button>
        );
      })}
    </div>
  );
}

/** Pied de table sombre et collant : totaux sur tout le filtre (jamais une somme de page). */
export function TableFooterTotals({ totals, caption }: { totals: ReadonlyArray<{ id: string; label: string; value: string | readonly string[]; hint?: string }>; caption?: string }) {
  if (totals.length === 0) return null;
  return (
    <div className="ay-tablefooter" role="status" aria-label={caption ?? 'Totaux'}>
      {caption !== undefined && <span className="ay-tablefooter__caption">{caption}</span>}
      {totals.map((total) => (
        <span key={total.id} className="ay-tablefooter__total" title={total.hint}>
          <span className="ay-tablefooter__label">{total.label}</span>
          {typeof total.value === 'string' ? (
            <strong>{total.value}</strong>
          ) : (
            // Un sous-total par devise ou unité, empilés (ADR-095).
            <span className="ay-tablefooter__values">
              {total.value.map((value) => (
                <strong key={value}>{value}</strong>
              ))}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}
